// Turns the service's field paths and values into words, for the publish diff and the compare view.
// Paths: `benefits[theft].label`, `pricingRule.addonRates.theft.capMinor`,
// `overrides[T-Rex].prices.monthly.12`, `settings.graceDays`.
import { formatBps, formatMinor } from "./format";

const TOP = {
  displayName: "Display name",
  tierRank: "Tier rank",
  termsVersion: "Terms version",
  tenures: "Monthly tenures",
  "display.tagline": "App: tagline",
  "display.bestValue": "App: Best value badge",
  "display.hero": "App: hero",
  "display.hero.title": "App: hero title",
  "display.hero.subtitle": "App: hero subtitle",
  "display.details": "App: plan details",
  "display.details.description": "App: description",
  "display.details.notCovered": "App: not covered",
  "display.termsUrl": "App: terms link",
};

const BENEFIT_FIELDS = {
  key: "Key",
  kind: "Kind",
  label: "Label",
  title: "Title",
  subtitle: "Subtitle",
  chargeModel: "How it is charged",
  termMonths: "Term in months",
  followsPlanState: "Follows the plan state",
  activation: "Activation",
  "unlock.type": "Unlock",
  "unlock.months": "Paid months before unlock",
  "unlock.immediateOnOneTime": "Unlocks at once when paid in one go",
  "unlock.on": "Unlock date",
};

const SETTINGS = {
  eligibilityDays: "Eligibility window (days)",
  upgradeWindowDays: "Upgrade window (days)",
  graceDays: "Grace period (days)",
  refundWindowDays: "Refund window (days)",
  refundRequiresNoUse: "Refund needs the plan to be unused",
  extensionGrant: "Extension cover",
  pendingExpiryHours: "Unpaid purchase expires after (hours)",
  inactive: "Inactive (off sale)",
  cohort: "Offered to",
  offerEndsOn: "Offer ends on",
};

const RULE = {
  baseBps: "Share of MRP",
  floorMinor: "Floor",
  capMinor: "Cap",
  roundingEndingMinor: "Rounding ending",
  roundingStepMinor: "Rounding step",
  monthlyUpliftBps: "Monthly uplift",
  monthlyRoundingEndingMinor: "Instalment rounding ending",
  monthlyRoundingStepMinor: "Instalment rounding step",
  taxRateBps: "Tax rate",
  cycleValueBasis: "Price the plan on",
  dealerMarginMinor: "Dealer margin (normal bikes)",
  dealerMarginOverrideMinor: "Dealer margin (override groups)",
};

const COUPON = {
  bps: "Coupon share of MRP",
  mode: "Coupon rounding",
  roundingEndingMinor: "Coupon rounding ending",
  roundingStepMinor: "Coupon rounding step",
  validityMonths: "Coupon validity (months)",
  basis: "Coupon based on",
};

const ENUM_LABELS = {
  cycle_mrp: "Cycle MRP",
  plan_price: "Plan price (incl. GST)",
  website_paid_price: "Website paid price (else MRP)",
  website_price: "Website price (every cycle, else MRP)",
  motor_extension: "Motor warranty extension",
  battery_extension: "Battery warranty extension",
  free_service: "Free service",
  theft: "Theft cover",
  roadside: "Roadside assistance",
  coupon: "Coupon",
  included: "Included in the plan price",
  monthly: "Paid monthly with the plan",
  upfront: "Paid upfront as an add-on",
  automatic: "Automatic",
  manual_ops: "Manual, by operations",
  purchase: "On purchase",
  paid_months: "After paid months",
  months_after_start: "Months after purchase",
  date: "On a date",
  truncate_major: "Round down to whole rupees",
  nearest_major: "Round to the nearest rupee",
  up_to_ending: "Round up to an ending",
  down_to_ending: "Round down to an ending",
  paid_months_capped: "Grows with paid months",
  full_on_completion: "Full once all months are paid",
};

// Bike segments (14 Oct 2026). A diff path names a segment by its key: "x_factor" reads "X Factor".
const segmentTitle = (key) =>
  key
    .split("_")
    .filter((word) => word !== "")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || key;

const SEGMENT_FIELDS = {
  name: "name",
  key: "key",
  models: "models",
  "prices.oneTimeMinor": "one-time price",
  dealerMarginMinor: "dealer margin",
};

// `pricingRule.segments`, `.defaultKey`, `.items` (the whole list), and one segment's field, keyed
// as `pricingRule.segments.items[desire].prices.oneTimeMinor` (or without `.items`).
const describeSegmentPath = (path) => {
  if (path === "pricingRule.segments" || path === "pricingRule.segments.items") {
    return "Bike segments";
  }
  if (path === "pricingRule.segments.defaultKey") {
    return "Bike segments: default segment";
  }
  const match = /^pricingRule\.segments(?:\.items)?\[(.+?)\](?:\(\d+\))?(?:\.(.+))?$/.exec(path);
  if (!match) {
    return null;
  }
  const segment = `Segment ${segmentTitle(match[1])}`;
  const rest = match[2];
  if (rest === undefined) {
    return segment;
  }
  let what = SEGMENT_FIELDS[rest] || rest;
  if (rest.startsWith("prices.monthly.")) {
    what = `${rest.slice("prices.monthly.".length)} months instalment`;
  } else if (rest.startsWith("prices.addons.")) {
    what = `${rest.slice("prices.addons.".length)} add-on price`;
  }
  return `${segment}: ${what}`;
};

export const describePath = (path) => {
  if (TOP[path]) {
    return TOP[path];
  }
  if (path.startsWith("pricingRule.segments")) {
    const segmentLabel = describeSegmentPath(path);
    if (segmentLabel !== null) {
      return segmentLabel;
    }
  }
  let match = /^benefits\[(.+?)\]\.(.+)$/.exec(path);
  if (match) {
    return `Benefit ${match[1]}: ${BENEFIT_FIELDS[match[2]] || match[2]}`;
  }
  match = /^settings\.(.+)$/.exec(path);
  if (match) {
    return `Settings: ${SETTINGS[match[1]] || match[1]}`;
  }
  match = /^pricingRule\.addonRates\.(.+)\.(bps|floorMinor|capMinor)$/.exec(path);
  if (match) {
    const field = { bps: "share of MRP", floorMinor: "floor", capMinor: "cap" }[match[2]];
    return `Add-on ${match[1]}: ${field}`;
  }
  match = /^pricingRule\.tenureFactors\.(.+)$/.exec(path);
  if (match) {
    return `Uplift for ${match[1]} months`;
  }
  match = /^pricingRule\.couponRule\.(.+)$/.exec(path);
  if (match) {
    return COUPON[match[1]] || `Coupon rule: ${match[1]}`;
  }
  if (path === "pricingRule.couponRule") {
    return "Coupon rule";
  }
  match = /^pricingRule\.(.+)$/.exec(path);
  if (match) {
    return `Pricing: ${RULE[match[1]] || match[1]}`;
  }
  match = /^overrides\[(.+?)\]\.prices\.(.+)$/.exec(path);
  if (match) {
    const group = match[1];
    const rest = match[2];
    let what = rest;
    if (rest === "oneTimeMinor") {
      what = "one-time price";
    } else if (rest === "couponValueMinor") {
      what = "coupon value";
    } else if (rest.startsWith("monthly.")) {
      what = `${rest.slice("monthly.".length)} months instalment`;
    } else if (rest.startsWith("addons.")) {
      what = `${rest.slice("addons.".length)} add-on price`;
    }
    return `Override ${group}: ${what}`;
  }
  return path;
};

const isMoneyPath = (path) =>
  /Minor$/.test(path) || /\.prices\.(monthly|addons)\./.test(path);
const isPercentPath = (path) =>
  /Bps$/.test(path) || /tenureFactors\./.test(path) || /couponRule\.bps$/.test(path) || /addonRates\..+\.bps$/.test(path);

// A whole segments value (or its list) on one line: "X Factor (default) ₹4,999.00, Desire ₹2,999.00".
const describeSegments = (value, currency) => {
  const items = Array.isArray(value) ? value : Array.isArray(value.items) ? value.items : [];
  if (items.length === 0) {
    return "None";
  }
  const defaultKey = Array.isArray(value) ? null : value.defaultKey;
  return items
    .map((item) => {
      const name = item && typeof item.name === "string" && item.name !== "" ? item.name : String(item?.key);
      const price = item && item.prices ? ` ${formatMinor(item.prices.oneTimeMinor, currency)}` : "";
      return `${name}${item && item.key === defaultKey ? " (default)" : ""}${price}`;
    })
    .join(", ");
};

export const formatChangeValue = (path, value, currency = "INR") => {
  if (value === undefined) {
    return "";
  }
  if (value === null) {
    return "None";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (/^pricingRule\.segments(\.items)?$/.test(path) && typeof value === "object") {
    return describeSegments(value, currency);
  }
  if (path === "pricingRule.segments.defaultKey" && typeof value === "string") {
    return segmentTitle(value);
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? "None" : value.join(", ");
  }
  if (typeof value === "number") {
    if (isMoneyPath(path)) {
      return formatMinor(value, currency);
    }
    if (isPercentPath(path)) {
      return formatBps(value);
    }
    return String(value);
  }
  if (typeof value === "string") {
    return ENUM_LABELS[value] || (value === "" ? "(empty)" : value);
  }
  // A hero (title and subtitle) reads as one line.
  if (typeof value === "object" && typeof value.title === "string") {
    return typeof value.subtitle === "string" ? `${value.title} / ${value.subtitle}` : value.title;
  }
  return JSON.stringify(value);
};
